import React, { useState, useEffect, useRef } from 'react';
import { FaMapMarkerAlt, FaLock } from 'react-icons/fa';
import { branches, getSelectedBranch, setSelectedBranch, getUpdatedBranches } from '../utils/branchesData';
import './BranchSelector.css';

function BranchSelector() {
  const [selectedBranch, setSelectedBranchState] = useState(getSelectedBranch());
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 'auto', left: 'auto' });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const branch = getSelectedBranch();
    setSelectedBranchState(branch);
  }, []);

  // Listen for branch updates from admin panel
  useEffect(() => {
    const handleBranchUpdate = () => {
      const branch = getSelectedBranch();
      setSelectedBranchState(branch);
    };

    // Listen for custom events when branches are updated
    window.addEventListener('branchesUpdated', handleBranchUpdate);
    
    return () => {
      window.removeEventListener('branchesUpdated', handleBranchUpdate);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Close dropdown when pressing Escape
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    // Recalculate position on scroll
    const handleScroll = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 300; // Approximate dropdown height
      
      let top = buttonRect.bottom + 8;
      let left = buttonRect.left;
      
      // Check if dropdown would go off screen
      if (top + dropdownHeight > viewportHeight) {
        // Position above the button instead
        top = buttonRect.top - dropdownHeight - 8;
      }
      
      // Check if dropdown would go off screen horizontally
      if (left + 300 > window.innerWidth) {
        left = window.innerWidth - 320; // 300px width + 20px margin
      }
      
      setDropdownPosition({ top: `${top}px`, left: `${left}px` });
    }
  };

  const handleToggleDropdown = () => {
    if (!isOpen) {
      calculateDropdownPosition();
    }
    setIsOpen(!isOpen);
  };

  const handleBranchChange = (branch) => {
    if (!branch.enabled) return; // Can't select disabled branches
    
    setSelectedBranch(branch.id);
    setSelectedBranchState(branch);
    setIsOpen(false);
    // Trigger a custom event to notify other components
    window.dispatchEvent(new CustomEvent('branchChanged', { detail: branch }));
  };

  return (
    <div className="branch-selector" ref={dropdownRef}>
      <button 
        ref={buttonRef}
        className="branch-selector-button" 
        onClick={handleToggleDropdown}
      >
        <FaMapMarkerAlt />
        <span>{selectedBranch.city}</span>
      </button>
      
      {isOpen && (
        <div 
          className="branch-selector-dropdown"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left
          }}
        >
          <div className="branch-selector-header">Select Your Nearest Branch</div>
          {getUpdatedBranches().map((branch) => (
            <button
              key={branch.id}
              className={`branch-option ${selectedBranch.id === branch.id ? 'selected' : ''} ${!branch.enabled ? 'disabled' : ''}`}
              onClick={() => handleBranchChange(branch)}
              disabled={!branch.enabled}
            >
              <div className="branch-option-content">
                <div className="branch-option-name">{branch.name}</div>
                <div className="branch-option-city">{branch.city}</div>
              </div>
              {!branch.enabled && (
                <div className="branch-option-lock">
                  <FaLock />
                </div>
              )}
            </button>
          ))}
          <div className="branch-selector-footer">
            Other branches coming soon
          </div>
        </div>
      )}
    </div>
  );
}

export default BranchSelector;


